import { Link } from "@remix-run/react";
import { useState } from "react";

import { SEO_ROUTE_GROUPS } from "~/utils/seo-routes";
import { ContactEmail } from "~/components/contact-email";
import prizoLogoColor from "~/assets/prizo-logo-color.svg";
import prizoLoogoWhite from "~/assets/prizoLoogoWhite.svg";
import footerEmailIcon from "~/assets/email-icon.svg";
import footerInstagramIcon from "~/assets/instagram-icon.svg";
import footerYoutubeIcon from "~/assets/youtube-icon.svg";
import footerLinkedinIcon from "~/assets/linkedin-icon.svg";

const fontClass = "font-['Public Sans'] font-sans";

/**
 * Tier-1 priority pages surfaced in the global footer. The footer link map no
 * longer lists every child route on every page (which created a flat, uniform
 * internal-link mesh and diluted equity); instead each hub shows only its
 * highest-value pages plus an "All {hub}" link. Every leaf remains discoverable
 * via its hub's card grid + the homepage link map, so nothing is orphaned.
 */
const FOOTER_PRIORITY = new Set<string>([
  "/seo/spin-the-wheel",
  "/solutions/reduce-cart-abandonment",
  "/solutions/increase-aov",
  "/compare/wheelio",
  "/compare/privy",
  "/compare/spin-wheel-apps",
]);

const navItems = [
  { text: "Play demo", href: "/#play" },
  { text: "How it works", href: "/#how" },
  { text: "Games", href: "/#games" },
  { text: "Pricing", href: "/#pricing" },
  { text: "FAQ", href: "/#faq" },
] as const;

interface SEOLayoutProps {
  children: React.ReactNode;
}

export function SEOLayout({ children }: SEOLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="bg-[#ffffff] box-border content-stretch flex flex-col items-start justify-start p-0 relative size-full overflow-x-hidden">
      {/* Header — matches the homepage nav (light, translucent) */}
      <header className="sticky top-0 left-0 right-0 z-[100] w-full backdrop-blur-[14px] bg-[rgba(251,250,255,0.82)] border-b border-[rgba(105,108,255,0.12)]">
        <div className="max-w-[1200px] mx-auto px-[22px] md:px-8 py-4 flex items-center justify-between">
          <Link to="/" aria-label="Prizo home" className="flex items-center shrink-0">
            <img alt="Prizo" className="block w-[68px] h-[45px]" src={prizoLogoColor} />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-[34px]">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`${fontClass} font-semibold text-[15px] text-[#4A4763] hover:text-[#696CFF] transition-colors`}
              >
                {item.text}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-[14px]">
            <Link
              to="/login"
              className={`${fontClass} font-bold text-[15px] text-[#14112E] hover:text-[#696CFF] transition-colors`}
            >
              Login
            </Link>
            <Link
              to="/register"
              className={`${fontClass} font-bold text-[15px] text-white bg-[#14112E] px-5 py-[11px] rounded-xl hover:opacity-90 transition-opacity`}
              style={{ boxShadow: "0 10px 24px -10px rgba(20,17,46,.5)" }}
            >
              Get started
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            aria-label="Menu"
            className="md:hidden p-2 relative"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span
                className={`bg-[#14112E] block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${mobileMenuOpen ? "rotate-45 translate-y-1" : "-translate-y-0.5"}`}
              />
              <span
                className={`bg-[#14112E] block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${mobileMenuOpen ? "opacity-0" : "opacity-100"}`}
              />
              <span
                className={`bg-[#14112E] block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${mobileMenuOpen ? "-rotate-45 -translate-y-1" : "translate-y-0.5"}`}
              />
            </div>
          </button>
        </div>

        {/* Mobile menu drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden px-[22px] pb-[22px] pt-2 border-t border-[rgba(105,108,255,0.12)] flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`${fontClass} font-semibold text-[16px] text-[#4A4763] py-3 px-1.5 hover:text-[#696CFF] transition-colors`}
              >
                {item.text}
              </Link>
            ))}
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className={`${fontClass} font-bold text-[16px] text-[#14112E] py-3 px-1.5`}
            >
              Login
            </Link>
            <Link
              to="/register"
              onClick={() => setMobileMenuOpen(false)}
              className={`${fontClass} font-bold text-[16px] text-white text-center bg-[#14112E] px-5 py-[13px] rounded-xl mt-1.5`}
            >
              Get started
            </Link>
          </div>
        )}
      </header>

      <main className="flex-1 w-full">{children}</main>

      {/* Footer */}
      <footer className="bg-[#2c2c2c] relative w-full border-t-2 border-[rgba(255,255,255,0.24)]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-[100px] py-7 md:pt-[60px] md:pb-8">
          {/* SEO link map — gives crawlers (and users) a path to every marketing page */}
          <nav
            aria-label="Site links"
            className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8 mb-10 pb-10 border-b border-[rgba(255,255,255,0.12)]"
          >
            {SEO_ROUTE_GROUPS.map((group) => {
              const priorityRoutes = group.routes.filter((r) => FOOTER_PRIORITY.has(r.path));
              return (
                <div key={group.title}>
                  <h4 className={`${fontClass} text-white text-[15px] mb-3`}>
                    <Link to={group.hubPath} className="hover:text-white/90 transition-colors">
                      {group.title}
                    </Link>
                  </h4>
                  <ul className="flex flex-col gap-2">
                    {priorityRoutes.map((route) => (
                      <li key={route.path}>
                        <Link
                          to={route.path}
                          className={`${fontClass} text-[13px] text-[rgba(255,255,255,0.7)] hover:text-white transition-colors`}
                        >
                          {route.label}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link
                        to={group.hubPath}
                        className={`${fontClass} text-[13px] text-[rgba(255,255,255,0.55)] hover:text-white transition-colors`}
                      >
                        All {group.title} →
                      </Link>
                    </li>
                  </ul>
                </div>
              );
            })}
          </nav>

          <div className="flex flex-col items-center gap-4 mb-7 md:hidden">
            <Link to="/">
              <img src={prizoLoogoWhite} alt="Prizo Logo" className="h-12 w-[73px]" />
            </Link>
            <div className="text-center">
              <p className={`${fontClass} text-white text-[14px] mb-6`}>
                Sign up now to get notified about latest offers & games!
              </p>
              <div className="flex flex-col gap-4 w-full max-w-[400px]">
                <div className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.12)] rounded-2xl">
                  <input
                    type="email"
                    placeholder="Your Email Address"
                    className={`${fontClass} w-full h-10 px-4 bg-transparent text-[rgba(255,255,255,0.8)] text-[14px] placeholder-[rgba(255,255,255,0.8)] outline-none`}
                  />
                </div>
                <button className={`${fontClass} h-10 bg-[#696cff] text-white rounded-2xl text-[14px]`}>
                  Subscribe Now
                </button>
              </div>
            </div>
          </div>

          <div className="hidden md:flex justify-between mb-10">
            <div className="flex flex-col gap-6 max-w-[320px]">
              <Link to="/">
                <img src={prizoLoogoWhite} alt="Prizo Logo" className="h-12 w-[73px]" />
              </Link>
              <div className="text-center">
                <p className={`${fontClass} text-white text-[14px] mb-6`}>
                  Sign up now to get notified about latest offers & games!
                </p>
                <div className="flex flex-col gap-4 w-full">
                  <div className="bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.12)] rounded-2xl">
                    <input
                      type="email"
                      placeholder="Your Email Address"
                      className={`${fontClass} w-full h-10 px-4 bg-transparent text-[rgba(255,255,255,0.8)] text-[14px] placeholder-[rgba(255,255,255,0.8)] outline-none`}
                    />
                  </div>
                  <button className={`${fontClass} h-10 bg-[#696cff] text-white rounded-2xl text-[14px]`}>
                    Subscribe Now
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-20">
              <div className="w-[200px]">
                <h4 className={`${fontClass} text-white text-[16px] mb-4`}>Pages</h4>
                <div className="flex flex-col gap-2 text-[14px] text-[rgba(255,255,255,0.8)]">
                  <Link to="/#play" className="text-left hover:text-white transition-colors">
                    Play demo
                  </Link>
                  <Link to="/#how" className="text-left hover:text-white transition-colors">
                    How it works
                  </Link>
                  <Link to="/#games" className="text-left hover:text-white transition-colors">
                    Games
                  </Link>
                  <Link to="/#pricing" className="text-left hover:text-white transition-colors">
                    Pricing
                  </Link>
                  <Link to="/faq" className="text-left hover:text-white transition-colors">
                    FAQ
                  </Link>
                </div>
              </div>
              <div>
                <h4 className={`${fontClass} text-white text-[16px] mb-4`}>Contact</h4>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <img src={footerEmailIcon} alt="" className="w-5 h-5" />
                    <ContactEmail
                      className={`${fontClass} text-[14px] text-[rgba(255,255,255,0.8)]`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 mb-7 md:hidden">
            <div className="w-[200px]">
              <h4 className={`${fontClass} text-white text-[16px] mb-3`}>Pages</h4>
              <div className="flex flex-col gap-2 text-[14px] text-[rgba(255,255,255,0.8)]">
                <Link to="/#play" className="text-left hover:text-white transition-colors">
                  Play demo
                </Link>
                <Link to="/#how" className="text-left hover:text-white transition-colors">
                  How it works
                </Link>
                <Link to="/#games" className="text-left hover:text-white transition-colors">
                  Games
                </Link>
                <Link to="/#pricing" className="text-left hover:text-white transition-colors">
                  Pricing
                </Link>
                <Link to="/faq" className="text-left hover:text-white transition-colors">
                  FAQ
                </Link>
              </div>
            </div>
            <div>
              <h4 className={`${fontClass} text-white text-[16px] mb-3`}>Contact</h4>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <img src={footerEmailIcon} alt="" className="w-5 h-5" />
                  <ContactEmail
                    className={`${fontClass} text-[14px] text-[rgba(255,255,255,0.8)]`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="h-[1px] bg-[rgba(255,255,255,0.12)] w-full mb-4 md:mb-10" />

          <div className="md:flex md:justify-between md:items-center">
            <div className="flex flex-col items-center gap-4 md:hidden">
              <span className={`${fontClass} text-white text-[12px]`}>© 2025 prizo.io</span>
              <div className="flex gap-6 text-[12px] text-white">
                <Link to="/privacy" className={fontClass}>
                  Privacy Policy
                </Link>
                <Link to="/terms" className={fontClass}>
                  Terms & Conditions
                </Link>
                <Link to="/faq" className={fontClass}>
                  FAQ
                </Link>
              </div>
              <div className="flex gap-10">
                <a
                  href="https://www.instagram.com/prizo.io?igsh=MWYzaXQ3ZTA2eDRqdw=="
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={footerInstagramIcon} alt="Instagram" className="w-5 h-5" />
                </a>
                <a
                  href="https://www.youtube.com/watch?v=OBSB40Cub70&ab_channel=PRIZO"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={footerYoutubeIcon} alt="YouTube" className="w-7 h-5" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer">
                  <img src={footerLinkedinIcon} alt="LinkedIn" className="w-6 h-5" />
                </a>
              </div>
            </div>
            <div className="hidden md:flex md:items-center md:gap-6">
              <span className={`${fontClass} text-white text-[14px]`}>© 2025 prizo.io</span>
              <div className="w-[1px] h-[22px] bg-[rgba(255,255,255,0.12)]" />
              <Link to="/privacy" className={`${fontClass} text-white text-[14px]`}>
                Privacy Policy
              </Link>
              <Link to="/terms" className={`${fontClass} text-white text-[14px]`}>
                Terms & Conditions
              </Link>
              <Link to="/faq" className={`${fontClass} text-white text-[14px]`}>
                FAQ
              </Link>
            </div>
            <div className="hidden md:flex md:gap-10">
              <a
                href="https://www.instagram.com/prizo.io?igsh=MWYzaXQ3ZTA2eDRqdw=="
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={footerInstagramIcon} alt="Instagram" className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/watch?v=OBSB40Cub70&ab_channel=PRIZO"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={footerYoutubeIcon} alt="YouTube" className="w-7 h-5" />
              </a>
              <a href="#" target="_blank" rel="noopener noreferrer">
                <img src={footerLinkedinIcon} alt="LinkedIn" className="w-6 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
